import * as React from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import { IBillToLocationParams } from '@user/store/locations/typings'
import Box from '@mui/material/Box'
import { Pagination, SideDrawerForm } from '@sales-center/components'
import { LargeLocationCard } from './LargeLocationCard'
import { BillToLocation } from '@user/components/EntityCards/Locations/BillToLocation'
import { selectAllBillToLocations } from '@user/store/entityLocations/slice'
import { useSelector } from 'react-redux'
import { useCallback, useState } from 'react'
import { addServiceLocations } from '@user/store/entityLocations/api'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { toast } from '@sales-center/components'
import { useGetBillToLocationOnMount } from '@user/store/entityLocations/hooks/useGetBillToLocationOnMount'
import Typography from '@mui/material/Typography'
import LinearProgress from '@mui/material/LinearProgress'
import { getLeadDetail } from '@user/store/lead/api'
import { getOpportunityDetail } from '@user/store/opportunity/api'
import { ENTITY_TYPES } from '@sales-center/common'
import { externalAppTimeoutError } from '@user/store/utilities/functions'

const getStyles = (theme: Theme) => ({
  drawer: {
    overflow: 'auto',
    height: 'auto',
  },

  backGround: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(5),
    height: 'auto',
  },

  backGroundGray: {
    backgroundColor: '#f9f9f9',
  },

  title: { fontFamily: 'Source Sans Pro SemiBold' },

  resultsCountLabel: {
    margin: `${theme.spacing(1.2)} 0`,
  },
})

export const AddExistingServiceLocationForm: React.FC<IBillToLocationParams> = (
  props,
) => {
  const { billToLocation, onClose, entityType, entityId } = props

  const dispatch = useAppDispatch()
  const takeSize = 3
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  const [selectedLocations, setLocations] = useState<string[]>([])

  const [pageState, setPageState] = useState({
    page: 1,
    skip: 0,
    take: takeSize,
  })

  const { billTolocationsStatus, serviceLocations } =
    useGetBillToLocationOnMount(
      entityId,
      entityType,
      pageState.skip,
      pageState.take,
    )

  const onChangePage = useCallback(
    (_, value) => {
      setPageState({
        page: value,
        skip: (value - 1) * takeSize,
        take: takeSize,
      })
    },
    [setPageState, takeSize],
  )

  const onChecked = useCallback(
    (id: string, checked: boolean) => {
      if (checked) {
        setLocations(selectedLocations.concat(id))
      } else {
        setLocations(selectedLocations.filter((x) => x !== id))
      }
    },
    [selectedLocations],
  )

  const onSubmit = useCallback(async () => {
    setSubmitting(true)
    const result = await dispatch(
      addServiceLocations({
        entityId,
        entityType,
        serviceLocations: selectedLocations,
      }),
    )

    if (addServiceLocations.fulfilled.match(result)) {
      toast.success(
        selectedLocations.length === 1
          ? '1 Service Location has been added successfully.'
          : `${selectedLocations.length} Service Locations have been added successfully.`,
      )

      if (entityType === ENTITY_TYPES.Lead) {
        dispatch(getLeadDetail(entityId))
      } else {
        dispatch(getOpportunityDetail(entityId))
      }

      onClose()
      setSubmitting(false)
    } else {
      if (!externalAppTimeoutError(result.payload)) {
        toast.error('We are unable to add Service Locations at this time.')
      }

      setSubmitting(false)
    }
  }, [selectedLocations, entityId, entityType, onClose, dispatch])

  const billToLocations = useSelector(selectAllBillToLocations)
  const titleLabel = `${selectedLocations.length} / ${
    serviceLocations ? serviceLocations.count : ''
  } Service Locations Added`
  const styles = getStyles(useTheme())
  return (
    <>
      <SideDrawerForm
        disableSave={selectedLocations.length === 0 || isSubmitting}
        onSave={onSubmit}
        onCancel={onClose}
        sx={styles.drawer}
        containerTop="122px"
      >
        <Box>
          <Box m={2} mb={3}>
            <Typography sx={styles.title}>
              Choose one or more service locations related to this Bill-to
            </Typography>
          </Box>
          <Box m={2}>
            {billToLocation && (
              <BillToLocation
                entityBranchId={billToLocations[0].branchId + ''}
                {...props}
                canUpdateEntity={false}
                billToLocation={billToLocations[0]}
                readonly={true}
                variant="card"
              />
            )}
          </Box>
          <Box
            mb={2}
            sx={{
              ...styles.backGround,
              ...(serviceLocations?.count !== 0 && styles.backGroundGray),
            }}
          >
            {serviceLocations?.count !== 0 && (
              <Box>
                <Typography sx={styles.title}>{titleLabel}</Typography>
              </Box>
            )}

            {billTolocationsStatus == 'pending' ? (
              <LinearProgress />
            ) : serviceLocations?.count === 0 ? (
              <Box sx={styles.resultsCountLabel}>
                No associated locations found
              </Box>
            ) : (
              serviceLocations?.items?.map((x) => (
                <Box key={x.externalLinkId} p={1}>
                  <LargeLocationCard
                    location={x}
                    billingLocation={false}
                    isSelected={
                      x.externalLinkId !== null &&
                      selectedLocations.includes(x.externalLinkId)
                    }
                    onSelected={onChecked}
                  />
                </Box>
              ))
            )}
          </Box>
          <Box
            display="flex"
            justifyContent="flex-end"
            mt={1}
            sx={{ position: 'relative', bottom: '10px' }}
          >
            <Pagination
              page={pageState.page}
              pages={
                serviceLocations
                  ? Math.ceil(serviceLocations.count / pageState.take)
                  : 0
              }
              onChangePage={onChangePage}
            />
          </Box>
        </Box>
      </SideDrawerForm>
    </>
  )
}
AddExistingServiceLocationForm.displayName = 'AddExistingServiceLocationForm'
